import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Stethoscope, LayoutDashboard, ClipboardList, X, Mail, Phone } from "lucide-react";

const JOTFORM_URL = "https://airtable.com/app51tDv4LNDemTGS/pag9rTXK5ZJ9xZnV0/form";
const JOTFORM_EMBED_URL = "https://airtable.com/embed/app51tDv4LNDemTGS/pag9rTXK5ZJ9xZnV0/form";
const CONTACT_EMAIL = "referrals@goatmarketingroup.com";
const CONTACT_PHONE_DISPLAY = "(214) 444-8299";
const CONTACT_PHONE_TEL = "+12144448299";
const COACH_DELAY_MS = 7000; // show the "Refer a Client" hint after ~7s

export default function Header({ total }) {
  const [referOpen, setReferOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);

  // Show a one-time coach-mark pointing at the Refer button after a short delay.
  useEffect(() => {
    if (sessionStorage.getItem("refer_coach_seen")) return;
    const t = setTimeout(() => setCoachOpen(true), COACH_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismissCoach = () => {
    setCoachOpen(false);
    sessionStorage.setItem("refer_coach_seen", "1");
    window.dispatchEvent(new Event("refer-coach-closed"));
  };

  const openRefer = () => {
    dismissCoach();
    setReferOpen(true);
  };

  return (
    <header
      data-testid="app-header"
      className="relative flex items-center justify-between gap-3 px-4 sm:px-8 py-2.5 min-h-[68px] shrink-0 bg-[#0B132B] border-b border-white/10 z-20"
    >
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px]"
        style={{ background: "linear-gradient(90deg, #291235 0%, #6d3a82 50%, #291235 100%)" }}
      />
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-lg bg-gradient-to-br from-indigo-600 to-[#291235] flex items-center justify-center shadow-lg">
          <Stethoscope className="w-5 h-5 text-white" />
        </div>
        <div className="leading-tight min-w-0">
          <h1 className="font-heading text-[15px] leading-tight sm:text-xl font-bold tracking-tight text-white">
            GOAT Personal Injury Network
          </h1>
          <p className="text-[11px] sm:text-xs text-slate-400 tracking-wide flex items-center gap-1.5 mt-0.5">
            <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">Texas &amp; Oklahoma Provider Network
            {typeof total === "number" && (
              <span className="ml-1 text-slate-500">· {total} locations</span>
            )}</span>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <div className="relative">
          {coachOpen && (
            <span className="pointer-events-none absolute inset-0 rounded-lg bg-orange-400/60 animate-ping" aria-hidden="true" />
          )}
          <button
            data-testid="refer-client-btn"
            onClick={openRefer}
            className={`relative flex items-center gap-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 px-3.5 py-2 rounded-lg transition-all duration-200 shadow-lg shadow-orange-500/20 ${
              coachOpen ? "ring-2 ring-orange-300 ring-offset-2 ring-offset-[#0B132B]" : ""
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            <span className="hidden sm:inline">Refer a Client</span>
          </button>

          {coachOpen && (
            <div
              data-testid="refer-coach-mark"
              className="absolute top-full right-0 mt-3 z-50 w-60 coach-pop"
            >
              {/* arrow pointing up to the button */}
              <span className="absolute -top-1.5 right-5 w-3 h-3 rotate-45 bg-[#0B132B]/90 border-l border-t border-orange-400/60" aria-hidden="true" />
              <div className="rounded-xl bg-[#0B132B]/90 backdrop-blur-md border border-orange-400/50 shadow-2xl shadow-orange-500/20 p-3.5">
                <button
                  data-testid="refer-coach-close"
                  onClick={dismissCoach}
                  className="absolute top-2 right-2 text-slate-400 hover:text-white"
                  aria-label="Dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="flex items-start gap-2.5 pr-3">
                  <span className="w-8 h-8 shrink-0 rounded-lg bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                    <ClipboardList className="w-4 h-4 text-white" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading text-sm font-bold text-white leading-tight">Refer a Client</p>
                    <p className="text-xs text-slate-300 leading-snug mt-0.5">
                      Tap the orange button above to send us a referral in seconds.
                    </p>
                    <div className="mt-2 flex flex-col gap-1.5">
                      <button
                        data-testid="refer-coach-cta"
                        onClick={openRefer}
                        className="text-xs font-bold text-orange-300 hover:text-orange-200 underline underline-offset-2 text-left"
                      >
                        Refer now →
                      </button>
                      <a
                        data-testid="refer-coach-text"
                        href={`sms:${CONTACT_PHONE_TEL}`}
                        onClick={dismissCoach}
                        title={CONTACT_PHONE_DISPLAY}
                        className="group relative inline-flex w-fit text-xs text-slate-300 hover:text-white"
                      >
                        or send via text
                        <span className="pointer-events-none absolute bottom-full left-0 mb-1.5 whitespace-nowrap rounded-md bg-orange-500 px-2 py-1 text-[11px] font-bold text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
                          {CONTACT_PHONE_DISPLAY}
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <button
          data-testid="contact-us-btn"
          onClick={() => setContactOpen(true)}
          className="flex items-center gap-2 text-sm font-semibold text-white bg-[#1f3a6e] hover:bg-[#27497f] px-3.5 py-2 rounded-lg border border-white/10 transition-all duration-200"
        >
          <Mail className="w-4 h-4" />
          <span className="hidden sm:inline">Contact Us</span>
        </button>
        <Link
          to="/admin"
          data-testid="header-admin-link"
          className="flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white px-3.5 py-2 rounded-lg border border-white/10 hover:border-white/30 hover:bg-white/5 transition-all duration-200"
        >
          <LayoutDashboard className="w-4 h-4" />
          <span className="hidden sm:inline">Admin</span>
        </Link>
      </div>

      {referOpen && (
        <div
          data-testid="referral-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6"
          onClick={() => setReferOpen(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-3xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 bg-[#0B132B] border-b border-white/10 shrink-0">
              <div className="flex items-center gap-2 text-white">
                <ClipboardList className="w-5 h-5 text-orange-400" />
                <span className="font-heading font-bold">Personal Injury Referral Form</span>
              </div>
              <div className="flex items-center gap-3">
                <a
                  data-testid="referral-newtab-link"
                  href={JOTFORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-slate-300 hover:text-white underline underline-offset-2"
                >
                  Open in new tab
                </a>
                <button
                  data-testid="referral-modal-close"
                  onClick={() => setReferOpen(false)}
                  className="text-slate-300 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <iframe
              data-testid="referral-iframe"
              title="Personal Injury Referral Form"
              src={JOTFORM_EMBED_URL}
              className="flex-1 w-full border-0 bg-white"
              allow="geolocation; microphone; camera; fullscreen; payment"
            />
          </div>
        </div>
      )}

      {contactOpen && (
        <div
          data-testid="contact-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setContactOpen(false)}
        >
          <div
            className="bg-[#111D4A] border border-white/10 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 bg-[#0B132B] border-b border-white/10">
              <div className="flex items-center gap-2 text-white">
                <Phone className="w-5 h-5 text-orange-400" />
                <span className="font-heading font-bold">Contact Us</span>
              </div>
              <button data-testid="contact-modal-close" onClick={() => setContactOpen(false)} className="text-slate-300 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <a
                data-testid="contact-email-link"
                href={`mailto:${CONTACT_EMAIL}`}
                className="flex items-center gap-3 p-3.5 rounded-xl bg-[#1A275B] hover:bg-[#22305f] border border-white/5 hover:border-white/20 transition-all duration-200"
              >
                <span className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-blue-300" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Email Us</p>
                  <p className="text-sm text-white font-medium truncate">{CONTACT_EMAIL}</p>
                </div>
              </a>
              <a
                data-testid="contact-phone-link"
                href={`tel:${CONTACT_PHONE_TEL}`}
                className="flex items-center gap-3 p-3.5 rounded-xl bg-[#1A275B] hover:bg-[#22305f] border border-white/5 hover:border-white/20 transition-all duration-200"
              >
                <span className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-orange-300" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Text or Call</p>
                  <p className="text-sm text-white font-medium">{CONTACT_PHONE_DISPLAY}</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
